import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Delete } from "lucide-react";

export type KeyStatus = "unused" | "correct" | "present" | "absent";

interface KeyboardProps {
  keyStatuses: Record<string, KeyStatus>;
  onKeyPress: (key: string) => void;
  onEnter: () => void;
  onBackspace: () => void;
  disabled?: boolean;
}

const KEYBOARD_ROWS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"]
];

export function Keyboard({ keyStatuses, onKeyPress, onEnter, onBackspace, disabled = false }: KeyboardProps) {
  const getKeyClasses = (key: string) => {
    const status = keyStatuses[key] || "unused";
    const baseClasses = cn(
      "h-[50px] sm:h-[52px] md:h-[54px] md:landscape:h-[52px] lg:landscape:h-[58px]",
      "rounded-[4px]",
      "shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_1px_2px_rgba(0,0,0,0.1)]",
      "p-0 px-1 box-border font-bold transition-all duration-100",
      "md:hover:brightness-110 active:scale-95",
      "flex items-center justify-center"
    );
    
    if (key === "ENTER" || key === "BACKSPACE") {
      return cn(
        baseClasses, 
        "bg-key-special text-key-special-foreground hover:bg-key-special/90",
        "flex-[1.5]" // Proportional width across all screens
      );
    }
    
    const statusClasses = {
      unused: "bg-key-unused text-key-unused-foreground hover:bg-key-unused/90",
      correct: "bg-tile-correct text-tile-correct-foreground",
      present: "bg-tile-present text-tile-present-foreground",
      absent: "bg-tile-absent text-tile-absent-foreground"
    };
    
    return cn(baseClasses, statusClasses[status], "flex-1");
  };

  const handleKeyClick = (key: string) => {
    if (disabled) return;
    
    if (key === "ENTER") {
      onEnter();
    } else if (key === "BACKSPACE") {
      onBackspace();
    } else {
      onKeyPress(key);
    }
  };

  return (
    <div 
      role="group" 
      aria-label="On-screen keyboard"
      className={cn(
        "flex flex-col items-center w-full",
        "gap-[6px] sm:gap-2 md:gap-2 md:landscape:gap-1 lg:landscape:gap-1.5"
      )}
    >
      {KEYBOARD_ROWS.map((row, rowIndex) => (
        <div key={rowIndex} className={cn(
          "flex justify-center w-full max-w-full",
          "gap-[4px] sm:gap-2 md:gap-2 md:landscape:gap-1.5"
        )}>
          {row.map((key) => (
            <Button
              key={key}
              variant="default"
              className={getKeyClasses(key)}
              onClick={() => handleKeyClick(key)}
              disabled={disabled}
              aria-label={key === 'BACKSPACE' ? 'Backspace' : key === 'ENTER' ? 'Enter' : `Key ${key}`}
              data-keyboard-key="true"
            >
              {key === "BACKSPACE" ? (
                <Delete className="w-5 h-5" />
              ) : (
                <span className={
                  key === "ENTER" 
                    ? "text-xs font-semibold"
                    : "text-base font-semibold"
                }>
                  {key}
                </span>
              )}
            </Button>
          ))}
        </div>
      ))}
    </div>
  );
}